import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, ChevronLeft, Clock, Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import { createCheckoutSession, submitAssessment } from "@/lib/api";
import { track } from "@/lib/analytics";

// ─── Constants ───────────────────────────────────────────────────────────────
const TOTAL_QUESTIONS = 6;
const OFFER_MS = 30 * 60 * 1000;
const TIMER_LS_KEY = "fs_assess_diy_start";

// ─── Questions ────────────────────────────────────────────────────────────────
const QUESTIONS = [
  {
    text: "What best describes your business today?",
    options: [
      { label: "I'm validating a new business or offer",                                diy: 2, dfy: 0 },
      { label: "I'm consistently generating revenue and growing",                       diy: 1, dfy: 0 },
      { label: "My business is established and I'm focused on scaling operations",      diy: 0, dfy: 1 },
      { label: "I manage multiple teams, locations, or business units",                 diy: 0, dfy: 2 },
    ],
  },
  {
    text: "How much revenue does your business generate annually?",
    options: [
      { label: "Under $50,000",            diy: 2, dfy: 0 },
      { label: "$50,000–$250,000",         diy: 1, dfy: 0 },
      { label: "$250,000–$1,000,000",      diy: 0, dfy: 1 },
      { label: "$1,000,000+",              diy: 0, dfy: 2 },
    ],
  },
  {
    text: "What's your biggest bottleneck right now?",
    options: [
      { label: "Finding qualified leads",                    diy: 0, dfy: 1, constraint: "Lead Generation",    agent: "AI Lead Generation Agent"    },
      { label: "Following up with prospects consistently",   diy: 0, dfy: 1, constraint: "Lead Follow-up",     agent: "AI Sales Follow-Up Agent"     },
      { label: "Managing repetitive administrative work",    diy: 1, dfy: 0, constraint: "Administrative Work",agent: "AI Operations Assistant"       },
      { label: "Scaling without hiring more employees",      diy: 0, dfy: 1, constraint: "Scaling Operations", agent: "AI Client Acquisition System" },
    ],
  },
  {
    text: "Which outcome are you looking for?",
    options: [
      { label: "I want to learn how to build AI Employees myself",                               diy: 2, dfy: 0, intent: "diy" },
      { label: "I want better systems inside my business",                                        diy: 0, dfy: 1, intent: "dfy" },
      { label: "I want my business to generate more revenue without adding more staff",           diy: 0, dfy: 1, intent: "dfy" },
      { label: "I want someone to build the systems for me",                                      diy: 0, dfy: 2, intent: "dfy" },
    ],
  },
  {
    text: "How much time can you realistically dedicate each week to implementing AI?",
    options: [
      { label: "Less than 5 hours",    diy: 0, dfy: 2 },
      { label: "5–10 hours",           diy: 0, dfy: 1 },
      { label: "10–20 hours",          diy: 1, dfy: 0 },
      { label: "Whatever it takes",    diy: 2, dfy: 0 },
    ],
  },
  {
    text: "Which statement sounds most like you?",
    options: [
      { label: "I enjoy learning new skills and building things myself",                                          diy: 2, dfy: 0 },
      { label: "I'd rather focus on running my business while someone else builds the systems",                   diy: 0, dfy: 2 },
      { label: "I'm open to either option if it gets me results faster",                                          diy: 0, dfy: 1 },
      { label: "I just want the fastest path to solving my bottleneck",                                           diy: 0, dfy: 1 },
    ],
  },
];

// ─── Scoring ──────────────────────────────────────────────────────────────────
function computeResult(selByQ) {
  let diy = 0, dfy = 0;
  selByQ.forEach((idx, qi) => {
    if (idx == null) return;
    const opt = QUESTIONS[qi].options[idx];
    diy += opt.diy || 0;
    dfy += opt.dfy || 0;
  });
  if (diy === dfy) {
    const q4 = selByQ[3] != null ? QUESTIONS[3].options[selByQ[3]] : null;
    return q4?.intent === "diy" ? "diy" : "dfy";
  }
  return diy > dfy ? "diy" : "dfy";
}

// ─── Timer helper ─────────────────────────────────────────────────────────────
function fmtTime(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────
const CARD = {
  background: "rgba(5, 10, 28, 0.97)",
  border: "1px solid rgba(255,255,255,0.1)",
  backdropFilter: "blur(28px)",
  WebkitBackdropFilter: "blur(28px)",
  borderRadius: 20,
  boxShadow: "0 24px 80px rgba(0,0,0,0.8)",
};

function OptionCard({ label, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "14px 18px",
        borderRadius: 12,
        border: selected ? "1px solid #D4AF37" : "1px solid rgba(255,255,255,0.1)",
        background: selected ? "rgba(212,175,55,0.1)" : "rgba(255,255,255,0.04)",
        color: selected ? "#F5E27A" : "rgba(255,255,255,0.82)",
        cursor: "pointer",
        fontSize: 14.5,
        lineHeight: 1.5,
        fontFamily: "Manrope, sans-serif",
        fontWeight: selected ? 600 : 400,
        transition: "all 0.18s ease",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <span style={{
        flexShrink: 0,
        width: 18, height: 18,
        borderRadius: "50%",
        border: selected ? "2px solid #D4AF37" : "1px solid rgba(255,255,255,0.25)",
        background: selected ? "#D4AF37" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.18s",
      }}>
        {selected && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0a0f1e" }} />}
      </span>
      {label}
    </button>
  );
}

// Refinement: taller bar (5px) and more visible track
function ProgressBar({ current, total }) {
  const pct = (current / total) * 100;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", fontFamily: "Manrope, sans-serif", textTransform: "uppercase" }}>
          Question {current} of {total}
        </span>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontFamily: "Manrope, sans-serif" }}>
          {Math.round(pct)}% complete
        </span>
      </div>
      <div style={{ height: 5, background: "rgba(255,255,255,0.14)", borderRadius: 9999, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: "linear-gradient(90deg, #00E5FF, #0066FF)",
          borderRadius: 9999,
          transition: "width 0.4s ease",
        }} />
      </div>
    </div>
  );
}

function DataRow({ label, value, accent }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      padding: "13px 0",
      borderBottom: "1px solid rgba(255,255,255,0.07)",
      gap: 16,
    }}>
      <span style={{ fontSize: 12, letterSpacing: "0.15em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase", fontFamily: "Manrope, sans-serif" }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: accent || "#fff", fontFamily: "Manrope, sans-serif", textAlign: "right", flexShrink: 0, maxWidth: "45%" }}>
        {value}
      </span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Assessment() {
  const navigate = useNavigate();

  const [phase, setPhase]             = useState("questions");
  const [currentQ, setCurrentQ]       = useState(0);
  const [selByQ, setSelByQ]           = useState(Array(TOTAL_QUESTIONS).fill(null));
  const [result, setResult]           = useState(null);
  const [timerStart, setTimerStart]   = useState(null);
  const [timeLeft, setTimeLeft]       = useState(OFFER_MS);
  const [busy, setBusy]               = useState(false);
  const [checkoutErr, setCheckoutErr] = useState("");
  const [assessmentId, setAssessmentId] = useState(null);

  useEffect(() => { track("assessment_started"); }, []);

  useEffect(() => {
    if (result !== "diy" || !timerStart) return;
    const id = setInterval(() => {
      const rem = OFFER_MS - (Date.now() - timerStart);
      setTimeLeft(Math.max(0, rem));
    }, 1000);
    return () => clearInterval(id);
  }, [result, timerStart]);

  const selectOption = useCallback((idx) => {
    setSelByQ(prev => { const n = [...prev]; n[currentQ] = idx; return n; });
  }, [currentQ]);

  const goNext = useCallback(() => {
    if (currentQ < TOTAL_QUESTIONS - 1) {
      setCurrentQ(q => q + 1);
    } else {
      const r = computeResult(selByQ);
      setResult(r);
      setPhase("complete");
      track("assessment_completed", { result: r });
      submitAssessment({ answers: selByQ, recommendation: r })
        .then(({ assessment_id }) => setAssessmentId(assessment_id))
        .catch(() => { /* non-fatal — checkout still works without a linked assessment */ });
    }
  }, [currentQ, selByQ]);

  const goBack = useCallback(() => {
    if (currentQ > 0) setCurrentQ(q => q - 1);
  }, [currentQ]);

  const goSnapshot = useCallback(() => {
    setPhase("snapshot");
    track("ai_opportunity_snapshot_viewed");
  }, []);

  const goResult = useCallback(() => {
    setPhase("result");
    if (result === "diy") {
      const stored = parseInt(localStorage.getItem(TIMER_LS_KEY), 10);
      const now = Date.now();
      let start;
      if (stored && now - stored < OFFER_MS) {
        start = stored;
      } else {
        start = now;
        localStorage.setItem(TIMER_LS_KEY, String(start));
      }
      setTimerStart(start);
      setTimeLeft(Math.max(0, OFFER_MS - (now - start)));
      track("builder_recommendation_viewed");
    } else {
      track("operator_recommendation_viewed");
    }
  }, [result]);

  const handleEnroll = useCallback(async (packageId) => {
    const sessionPromise = createCheckoutSession({ package_id: packageId, assessment_id: assessmentId || undefined });
    setBusy(true);
    setCheckoutErr("");
    try {
      const { url } = await sessionPromise;
      window.location.replace(url);
    } catch (e) {
      setCheckoutErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setBusy(false);
    }
  }, [assessmentId]);

  const currentSel   = selByQ[currentQ];
  const canAdvance   = currentSel !== null;
  const q3opt        = selByQ[2] != null ? QUESTIONS[2].options[selByQ[2]] : null;
  const offerExpired = timeLeft === 0;

  return (
    <div style={{ minHeight: "100vh", background: "#000510", position: "relative" }}>
      <Nav />

      <div style={{
        minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "100px 16px 40px",
      }}>
        <div style={{ ...CARD, width: "100%", maxWidth: 580, position: "relative" }}>
          <div style={{ padding: "32px 28px 28px" }}>

            {/* ── PHASE: questions ── */}
            {phase === "questions" && (
              <>
                {/* Header */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 12px", borderRadius: 9999,
                    border: "1px solid rgba(0,229,255,0.3)", background: "rgba(0,229,255,0.06)",
                    marginBottom: 16,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#00E5FF", boxShadow: "0 0 8px #00E5FF" }} />
                    <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "#7DD3FC", fontFamily: "Manrope, sans-serif", fontWeight: 600 }}>
                      AI INFRASTRUCTURE ASSESSMENT
                    </span>
                  </div>
                  <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 700, lineHeight: 1.3, marginBottom: 6, fontFamily: "Manrope, sans-serif" }}>
                    Find Out Which AI Growth Path Fits Your Business
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13.5, lineHeight: 1.55, fontFamily: "Manrope, sans-serif" }}>
                    Take this 2-minute assessment to discover whether your business is better suited for a custom AI Infrastructure installation or learning how to build AI Employees yourself.
                  </p>
                </div>

                <ProgressBar current={currentQ + 1} total={TOTAL_QUESTIONS} />

                {/* Refinement: extra spacing between question and cards (28px bottom vs 16px before) */}
                <p style={{
                  color: "#fff", fontSize: 17, fontWeight: 600, lineHeight: 1.45,
                  margin: "22px 0 28px", fontFamily: "Manrope, sans-serif",
                }}>
                  {QUESTIONS[currentQ].text}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {QUESTIONS[currentQ].options.map((opt, idx) => (
                    <OptionCard
                      key={idx}
                      label={opt.label}
                      selected={currentSel === idx}
                      onClick={() => selectOption(idx)}
                    />
                  ))}
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 22, alignItems: "center" }}>
                  {currentQ > 0 && (
                    <button
                      onClick={goBack}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "10px 18px", borderRadius: 9999,
                        background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.6)", cursor: "pointer",
                        fontSize: 13, fontFamily: "Manrope, sans-serif", fontWeight: 600,
                      }}
                    >
                      <ChevronLeft size={14} /> Back
                    </button>
                  )}
                  <button
                    onClick={goNext}
                    disabled={!canAdvance}
                    style={{
                      flex: 1,
                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                      padding: "12px 24px", borderRadius: 9999,
                      background: canAdvance
                        ? "linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)"
                        : "rgba(255,255,255,0.07)",
                      border: canAdvance ? "1px solid rgba(212,175,55,0.5)" : "1px solid rgba(255,255,255,0.08)",
                      color: canAdvance ? "#0a0f1e" : "rgba(255,255,255,0.25)",
                      cursor: canAdvance ? "pointer" : "default",
                      fontSize: 13, fontWeight: 700, letterSpacing: "0.05em",
                      fontFamily: "Manrope, sans-serif",
                      transition: "all 0.2s",
                    }}
                  >
                    {currentQ < TOTAL_QUESTIONS - 1 ? "CONTINUE" : "SEE MY RESULTS"}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </>
            )}

            {/* ── PHASE: complete ── */}
            {phase === "complete" && (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{
                  width: 64, height: 64, borderRadius: "50%", margin: "0 auto 20px",
                  background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.4)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <CheckCircle size={30} color="#00E5FF" />
                </div>
                <h2 style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginBottom: 14, fontFamily: "Manrope, sans-serif" }}>
                  Congratulations!
                </h2>
                <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 15, lineHeight: 1.65, maxWidth: 420, margin: "0 auto 28px", fontFamily: "Manrope, sans-serif" }}>
                  You've just taken the first step toward systemizing your business and buying back your time.
                  Based on your responses, we've identified where AI can create the biggest impact inside your business.
                </p>
                <button
                  onClick={goSnapshot}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "14px 28px", borderRadius: 9999,
                    background: "linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)",
                    border: "1px solid rgba(212,175,55,0.5)",
                    color: "#0a0f1e", cursor: "pointer",
                    fontSize: 13.5, fontWeight: 700, letterSpacing: "0.04em",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  View My AI Opportunity Snapshot <ArrowRight size={15} />
                </button>
              </div>
            )}

            {/* ── PHASE: snapshot ── */}
            {phase === "snapshot" && (
              <>
                <div style={{ marginBottom: 22 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 12px", borderRadius: 9999,
                    border: "1px solid rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.07)",
                    marginBottom: 12,
                  }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "#D4AF37", fontFamily: "Manrope, sans-serif", fontWeight: 600 }}>
                      YOUR RESULTS
                    </span>
                  </div>
                  <h2 style={{ color: "#fff", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                    Your AI Opportunity Snapshot
                  </h2>
                </div>

                <div style={{
                  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 14, padding: "4px 18px 8px", marginBottom: 22,
                }}>
                  <DataRow label="Primary Constraint"               value={q3opt?.constraint || "—"} accent="#F5E27A" />
                  <DataRow label="Hours You Could Automate / Month" value="≈ 40–60 Hours"             accent="#00E5FF" />
                  <DataRow label="Recommended First AI Employee"    value={q3opt?.agent || "—"}       accent="#fff"    />
                  <DataRow label="Implementation Difficulty"        value="Easy"                       accent="#4ade80" />
                </div>

                <button
                  onClick={goResult}
                  style={{
                    width: "100%",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "14px 24px", borderRadius: 9999,
                    background: "linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)",
                    border: "1px solid rgba(212,175,55,0.5)",
                    color: "#0a0f1e", cursor: "pointer",
                    fontSize: 13.5, fontWeight: 700, letterSpacing: "0.04em",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  View My Recommended Path <ArrowRight size={15} />
                </button>
              </>
            )}

            {/* ── PHASE: result — DIY ── */}
            {phase === "result" && result === "diy" && (
              <>
                <div style={{ marginBottom: 6 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 12px", borderRadius: 9999,
                    border: "1px solid rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.07)",
                    marginBottom: 14,
                  }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "#D4AF37", fontFamily: "Manrope, sans-serif", fontWeight: 600 }}>
                      BUILDER FOUNDER · YOUR BEST NEXT STEP
                    </span>
                  </div>
                  <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 12, fontFamily: "Manrope, sans-serif" }}>
                    Congratulations.
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13.5, lineHeight: 1.65, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>
                    You've already taken the first step toward systemizing your business and buying back your time.
                    Based on your responses, your business is best aligned with:
                  </p>
                  <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, lineHeight: 1.5, marginBottom: 10, fontFamily: "Manrope, sans-serif", fontStyle: "italic" }}>
                    Based on your responses, here's the path that will create the fastest ROI for your business.
                  </p>
                  <p style={{ color: "#F5E27A", fontSize: 17, fontWeight: 700, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>
                    AI Employees Masterclass
                  </p>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13.5, lineHeight: 1.65, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>
                    You enjoy learning new systems. You want long-term leverage and high-income skills. You're comfortable implementing proven frameworks yourself.
                  </p>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13.5, lineHeight: 1.65, fontFamily: "Manrope, sans-serif" }}>
                    Instead of relying on expensive employees or multiple software subscriptions, you'll learn how to design, build, and deploy AI Employees that work inside your business 24/7.
                  </p>
                </div>

                {/* Pricing card */}
                <div style={{
                  background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.25)",
                  borderRadius: 14, padding: "18px 20px", margin: "18px 0",
                }}>
                  <p style={{ fontSize: 11, letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", fontFamily: "Manrope, sans-serif", textTransform: "uppercase", marginBottom: 8 }}>
                    Recommended Path
                  </p>
                  <p style={{ color: "#fff", fontWeight: 700, fontSize: 15, fontFamily: "Manrope, sans-serif", marginBottom: 12 }}>
                    AI Employees Masterclass
                  </p>

                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    {!offerExpired ? (
                      <>
                        <span className="assess-price-shimmer" style={{ fontSize: 36, fontWeight: 800, fontFamily: "Manrope, sans-serif" }}>
                          $897
                        </span>
                        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, textDecoration: "line-through", fontFamily: "Manrope, sans-serif" }}>
                          $997
                        </span>
                        <span style={{
                          padding: "2px 9px", borderRadius: 9999, fontSize: 10, fontWeight: 700,
                          background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)",
                          color: "#D4AF37", letterSpacing: "0.1em", fontFamily: "Manrope, sans-serif",
                        }}>
                          ASSESSMENT EXCLUSIVE
                        </span>
                      </>
                    ) : (
                      <span style={{ color: "#fff", fontSize: 30, fontWeight: 800, fontFamily: "Manrope, sans-serif" }}>
                        $997
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <Clock size={13} color={offerExpired ? "rgba(255,255,255,0.3)" : "#D4AF37"} />
                    {offerExpired ? (
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontFamily: "Manrope, sans-serif" }}>
                        Your assessment bonus has expired. Go back and retake the assessment for a new personalized recommendation and pricing window.
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontFamily: "Manrope, sans-serif" }}>
                        Your assessment bonus is reserved for the next{" "}
                        <span style={{ color: "#D4AF37", fontWeight: 700 }}>{fmtTime(timeLeft)}</span>
                      </span>
                    )}
                  </div>
                </div>

                {checkoutErr && (
                  <p style={{ color: "#f87171", fontSize: 12, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>{checkoutErr}</p>
                )}

                <button
                  onClick={() => { track("start_building_cta_clicked", { offer_expired: offerExpired }); handleEnroll(offerExpired ? "ai_agents_course_v1" : "ai_agents_assessment_v1"); }}
                  disabled={busy}
                  style={{
                    width: "100%",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "14px 24px", borderRadius: 9999,
                    background: busy ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)",
                    border: "1px solid rgba(212,175,55,0.5)",
                    color: busy ? "rgba(255,255,255,0.3)" : "#0a0f1e",
                    cursor: busy ? "default" : "pointer",
                    fontSize: 13.5, fontWeight: 700, letterSpacing: "0.05em",
                    fontFamily: "Manrope, sans-serif",
                    transition: "all 0.2s",
                  }}
                >
                  {busy ? <><Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> REDIRECTING…</> : <>START BUILDING <ArrowRight size={14} /></>}
                </button>
              </>
            )}

            {/* ── PHASE: result — DFY ── */}
            {phase === "result" && result === "dfy" && (
              <>
                <div style={{ marginBottom: 6 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 12px", borderRadius: 9999,
                    border: "1px solid rgba(0,229,255,0.3)", background: "rgba(0,229,255,0.06)",
                    marginBottom: 14,
                  }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "#7DD3FC", fontFamily: "Manrope, sans-serif", fontWeight: 600 }}>
                      OPERATOR FOUNDER · YOUR BEST NEXT STEP
                    </span>
                  </div>
                  <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 12, fontFamily: "Manrope, sans-serif" }}>
                    Congratulations.
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13.5, lineHeight: 1.65, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>
                    You've already taken the first step toward building a business that scales through systems instead of more hours.
                    Based on your responses, your business is an excellent candidate for:
                  </p>
                  <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, lineHeight: 1.5, marginBottom: 10, fontFamily: "Manrope, sans-serif", fontStyle: "italic" }}>
                    Based on your responses, here's the path that will create the fastest ROI for your business.
                  </p>
                  <p style={{ color: "#7DD3FC", fontSize: 17, fontWeight: 700, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>
                    Private AI Infrastructure Audit
                  </p>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13.5, lineHeight: 1.65, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>
                    Your biggest bottleneck isn't knowledge. It's time. Your business would benefit most from having AI infrastructure professionally designed and implemented.
                  </p>
                </div>

                {/* Checklist */}
                <div style={{
                  background: "rgba(0,229,255,0.04)", border: "1px solid rgba(0,229,255,0.15)",
                  borderRadius: 14, padding: "16px 18px", margin: "14px 0",
                }}>
                  <p style={{ fontSize: 11, letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", fontFamily: "Manrope, sans-serif", textTransform: "uppercase", marginBottom: 12 }}>
                    Recommended Path — Book a Private AI Infrastructure Audit
                  </p>
                  {[
                    "Analyze your biggest business constraint",
                    "Recommend the highest-impact AI Employees",
                    "Build a custom implementation roadmap",
                    "Create a six-month AI infrastructure strategy tailored to your business",
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: i < 3 ? 9 : 0 }}>
                      <span style={{
                        flexShrink: 0, marginTop: 2,
                        width: 18, height: 18, borderRadius: "50%",
                        background: "rgba(0,240,255,0.12)", border: "1px solid rgba(0,240,255,0.5)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <svg width="9" height="9" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#00F0FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13.5, lineHeight: 1.5, fontFamily: "Manrope, sans-serif" }}>{item}</span>
                    </div>
                  ))}

                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, fontFamily: "Manrope, sans-serif" }}>Investment: </span>
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: 22, fontFamily: "Manrope, sans-serif" }}>$397</span>
                  </div>
                </div>

                {checkoutErr && (
                  <p style={{ color: "#f87171", fontSize: 12, marginBottom: 10, fontFamily: "Manrope, sans-serif" }}>{checkoutErr}</p>
                )}

                <button
                  onClick={() => { track("book_infrastructure_audit_cta_clicked"); navigate("/audit/checkout", { state: { assessmentId } }); }}
                  disabled={busy}
                  style={{
                    width: "100%",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "14px 24px", borderRadius: 9999,
                    background: busy ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00E5FF 0%, #0066FF 100%)",
                    border: "1px solid rgba(0,229,255,0.5)",
                    color: busy ? "rgba(255,255,255,0.3)" : "#001018",
                    cursor: busy ? "default" : "pointer",
                    fontSize: 13.5, fontWeight: 700, letterSpacing: "0.05em",
                    fontFamily: "Manrope, sans-serif",
                    transition: "all 0.2s",
                  }}
                >
                  {busy ? <><Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> REDIRECTING…</> : <>BOOK MY INFRASTRUCTURE AUDIT <ArrowRight size={14} /></>}
                </button>
              </>
            )}

          </div>
        </div>
      </div>

      <style>{`
        @keyframes assess-gold-sweep {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .assess-price-shimmer {
          background: linear-gradient(90deg,
            #B8960C 0%, #D4AF37 20%, #F5E27A 40%, #D4AF37 55%, #B8960C 70%, #D4AF37 85%, #F5E27A 100%
          );
          background-size: 250% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: assess-gold-sweep 2.5s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
